// Libs
import classNames from 'classnames/bind';
import { Outlet, useLocation } from 'react-router-dom';
import { Alert, Button, Flex, IconRefreshLine, Spinner, Text, View } from '@instructure/ui';
// Components, Layouts, Pages
import { Header } from '~/layouts/main/components';
import { CanvasSessionNotice } from '~/components/common';
// Others
import packageJson from '../../../package.json';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { toastActions } from '~/thunks/toast/toastSlice';
// Styles, images, icons
import styles from './Main.module.scss';
import { getCourses } from '~/thunks/course/courseThunk';
import { DEFAULT_LIMIT, EMPTY_STRING } from '~/utils/constants/common';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { studentRoute } from '~/utils/routes/student';

const cx = classNames.bind(styles);

const Main = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base', 'common/course']);
  const dispatch = useAppDispatch();
  const location = useLocation();
  //#endregion Declare Hook

  //#region Selector
  const toasts = useAppSelector((state) => state.toastState.toasts);
  const { mainCourses, mainLoading, mainError } = useAppSelector((state) => state.courseState);
  //#endregion Selector

  //#region Declare State
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const isStudentPage = location.pathname.startsWith(studentRoute.base);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    handleGetCourses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudentPage]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleDismissAlert = (messageId: string) => {
    dispatch(toastActions.removeToast(messageId));
  };

  const handleGetCourses = useCallback(async () => {
    if (currentRole !== RoleEnum.TEACHER || !isStudentPage) return;

    await dispatch(getCourses({ limit: DEFAULT_LIMIT, isMainPage: true })).unwrap();
  }, [dispatch, currentRole, isStudentPage]);

  const mainContent = useMemo(() => {
    if (currentRole !== RoleEnum.TEACHER || !isStudentPage) {
      return <Outlet />;
    }

    if (mainLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (mainError) {
      return (
        <div className={cx('centerContainer')}>
          <Flex justifyItems='center'>
            <Button onClick={handleGetCourses} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
              {t('retry_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </div>
      );
    }

    if (mainCourses.length === 0) {
      return (
        <div className={cx('centerContainer')}>
          <Text>{t('course_detail_no_video_available', { ns: 'common/course' })}</Text>
        </div>
      );
    }

    return <Outlet />;
  }, [mainLoading, mainError, mainCourses.length, handleGetCourses, t, currentRole, isStudentPage]);
  //#endregion Handle Function

  return (
    <div id='mainLayout' className={cx('container')}>
      <header className={cx('header')}>
        <Header />
      </header>

      <CanvasSessionNotice />

      <main className={cx('content')}>
        <section className={cx('mainContent')}>{mainContent}</section>
      </main>

      <div className={cx('versionText')}>
        <Text variant='contentSmall'>{packageJson.version}</Text>
      </div>

      <View className={cx('containerAlert')}>
        {toasts.map((toast, index) => {
          return (
            <Alert
              key={index}
              variant={toast.type}
              renderCloseButtonLabel={toast.allowClose && 'Close'}
              open={true}
              margin='small 0'
              onDismiss={() => handleDismissAlert(toast.id)}
            >
              {toast.message}
            </Alert>
          );
        })}
      </View>
    </div>
  );
};

export default Main;
